import React, { useRef, useState } from 'react';
import { KatIcon, KatPopover, KatLink } from '@amzn/katal-react';
import Link, { LinkProps } from 'next/link';
import { Pages, NavigationCardTypes, MetricKeys } from 'src/constants/metrics';
import clickMetric from 'src/utils/clickRateMetricsPublisher';
import useResizeObserver from '@react-hook/resize-observer';
import ConditionalWrapper from '../ConditionalWrapper';

export interface NavigationCardProps {
  title: string;
  description: React.ReactNode;
  linkText: string;
  linkHref: string | LinkProps['href'];
  metricValue?: NavigationCardTypes;
  newTab?: boolean;
  katLink?: boolean;
}

const metricsHandler = (pageName: string, metricValue: string, linkHref: any) => {
  switch (metricValue) {
    case NavigationCardTypes.categories:
      clickMetric(pageName, MetricKeys.navigationCard, linkHref.query['categories[]'] as string);
      break;
    default:
      clickMetric(pageName, MetricKeys.navigationCard, metricValue);
      break;
  }
};

const popover = (
  children: React.ReactNode,
  description: React.ReactNode,
) => (
  <KatPopover
    kat-aria-behavior="tooltip"
    position="top"
    triggerType="hover"
    variant="tooltip"
  >
    {children}
    {description}
  </KatPopover>
);

const NavigationCard: React.FC<NavigationCardProps> = ({
  title,
  description,
  linkHref,
  linkText,
  metricValue,
  newTab,
  katLink,
}) => {
  const descRef = useRef<HTMLDivElement>(null);

  const [isClamped, setIsClamped] = useState(false);

  if (typeof window !== 'undefined') {
    useResizeObserver(document.body, () => {
      if (descRef.current) {
        const { scrollHeight, clientHeight } = descRef.current;
        const clamped = scrollHeight > clientHeight;
        if (clamped !== isClamped) setIsClamped(clamped);
      }
    });
  }

  const anchorProps: any = {
    'data-testid': 'navigation-card-link',
    className: 'w-fit font-medium',
    onClick: () => {
      if (metricValue) {
        metricsHandler(Pages.home, metricValue, linkHref);
      }
    },
  };

  if (newTab) {
    anchorProps.rel = 'noopener noreferrer';
    anchorProps.target = '__blank';
  }

  return (
    <div data-testid="navigation-card" className="relative h-[148px] rounded border border-solid border-kat-seattle bg-white py-4.5 px-5 shadow-[0px_2px_5px_1px_rgba(0,47,54,0.05)]">
      <div className="flex h-full flex-col justify-between">
        <div>
          <h3>{title}</h3>
          <ConditionalWrapper
            condition={isClamped}
            wrapper={(children) => popover(children, description)}
          >
            <div
              className="cursor-default text-kat-tarpon line-clamp-2"
              ref={descRef}
              slot="trigger"
            >
              {description}
            </div>
          </ConditionalWrapper>
        </div>
        {
          katLink
            ? (
              <KatLink
                href={`${linkHref}`}
                target="_blank"
                variant="link"
                {...anchorProps}
              >
                {linkText}
                <KatIcon className="pt-1 text-kat-eastern ltr:ml-0.5 rtl:mr-0.5" name="arrow-right" size="tiny" />
              </KatLink>
            )
            : (
              <Link
                href={linkHref}
                {...anchorProps}
              >
                {linkText}
                <KatIcon className="pt-1 text-kat-eastern ltr:ml-0.5 rtl:mr-0.5" name="arrow-right" size="tiny" />
              </Link>
            )
        }
      </div>
    </div>
  );
};

NavigationCard.defaultProps = {
  metricValue: undefined,
  newTab: false,
  katLink: false,
};

export default NavigationCard;
